import debounce from 'hs-lodash/debounce';
import I18n from 'I18n';
import { UNKNOWN_PARTICIPANT } from '../constants/CallParticipantTypes';
import { getUtteranceId } from './idHelpers';
import { TRANSCRIPT_UTTERANCES_CONTAINER_ID } from '../constants/ContainerIds';
import { extractTextFromReactNodes } from './htmlUtils';
export const TEXT_SCROLL_MIN_TIME_DIFF = 100; //ms
export const TEXT_SCROLL_MAX_TIME_DIFF = 300; //ms

export const getUnknownParticipantInfo = name => {
  return {
    participantName: name,
    participantEmail: null,
    participantId: '',
    participantType: UNKNOWN_PARTICIPANT,
    associatedObjectId: null,
    mappedSpeakerTrackId: null,
    mappedCategory: null
  };
};
export const getSpeakerInfo = (utteranceData, transcriptParticipants) => {
  const matchedParticipant = transcriptParticipants.find(participant => participant.mappedSpeakerTrackId === utteranceData.speakerTrackId);
  return matchedParticipant || getUnknownParticipantInfo(I18n.text('recordingReviewComponents.transcript.speakerName', {
    speakerTrackId: utteranceData.speakerTrackId
  }));
};
export const getFirstUnmatchedUtterance = (transcriptUtterances, transcriptParticipants) => {
  for (const utteranceData of transcriptUtterances) {
    const speakerInfo = getSpeakerInfo(utteranceData, transcriptParticipants);
    if (speakerInfo.mappedSpeakerTrackId === null || speakerInfo.mappedSpeakerTrackId === undefined) {
      const utteranceId = getUtteranceId(utteranceData, TRANSCRIPT_UTTERANCES_CONTAINER_ID);
      return utteranceId;
    }
  }
  return null;
};
export const scrollToId = ({
  containerId,
  childId,
  smooth = true,
  offsetMargin = 24,
  isNavButtonClickRef,
  callback = () => {}
}) => {
  const containerEl = document.getElementById(containerId);
  const childEl = document.getElementById(childId);
  if (containerEl && childEl) {
    const offsetTop = childId.includes('searchResult') ? childEl.offsetParent.offsetTop : childEl.offsetTop;
    const options = {
      behavior: smooth ? 'smooth' : 'auto',
      top: offsetTop - offsetMargin
    };
    let lastScrollTop = containerEl.scrollTop;
    let checksWithoutChange = 0;
    const monitorScroll = () => {
      if (lastScrollTop === containerEl.scrollTop) {
        checksWithoutChange++;
        if (checksWithoutChange > 5) {
          // after 5 continuous checks without a change, consider scrolling done
          if (isNavButtonClickRef && isNavButtonClickRef.current) {
            isNavButtonClickRef.current = false;
          }
          callback();
          return;
        }
      } else {
        checksWithoutChange = 0;
        lastScrollTop = containerEl.scrollTop;
      }
      requestAnimationFrame(monitorScroll);
    };
    containerEl.scroll(options);
    requestAnimationFrame(monitorScroll);
  }
};
export const scrollToIdDebounce = debounce(({
  containerId,
  childId,
  smooth = true,
  offsetMargin = 24,
  isNavButtonClickRef,
  callback = () => {}
}) => {
  scrollToId({
    containerId,
    childId,
    smooth,
    offsetMargin,
    isNavButtonClickRef,
    callback
  });
}, TEXT_SCROLL_MIN_TIME_DIFF, {
  maxWait: TEXT_SCROLL_MAX_TIME_DIFF
});
export function getActiveUtterance(transcript, currentTime) {
  if (!transcript || !transcript.length) return {};

  // Edge case for the first utterance
  if (!currentTime || currentTime <= transcript[0].startTimeSeconds) {
    return {
      utterance: transcript[0],
      index: 0
    };
  }

  // Edge case for the last utterance
  if (currentTime >= transcript[transcript.length - 1].startTimeSeconds) {
    return {
      utterance: transcript[transcript.length - 1],
      index: transcript.length - 1
    };
  }
  let leftUtteranceIndex = 0;
  let rightUtteranceIndex = transcript.length - 1;
  while (leftUtteranceIndex <= rightUtteranceIndex) {
    const midUtteranceIndex = Math.floor((leftUtteranceIndex + rightUtteranceIndex) / 2);
    const midUtterance = transcript[midUtteranceIndex];
    if (midUtterance.startTimeSeconds <= currentTime && midUtterance.endTimeSeconds >= currentTime) {
      return {
        utterance: midUtterance,
        index: midUtteranceIndex
      };
    } else if (midUtterance.endTimeSeconds < currentTime) {
      leftUtteranceIndex = midUtteranceIndex + 1;
    } else {
      rightUtteranceIndex = midUtteranceIndex - 1;
    }
  }

  // If we've reached here, it means that the currentTime is between two utterances.
  // Return the next utterance
  if (leftUtteranceIndex < transcript.length) {
    return {
      utterance: transcript[leftUtteranceIndex],
      index: leftUtteranceIndex
    };
  }
  return {};
}
export const getUtteranceAsPlainText = utteranceData => {
  if (typeof utteranceData.utterance === 'string') {
    return utteranceData.utterance;
  } else {
    return extractTextFromReactNodes(utteranceData.utterance);
  }
};